/**
 * Order-review model (R10 — S6 order review scene, showcase-conversation-synergy.md §5).
 *
 * Before place_order / send_spec_sheet the showcase renders "your selection": images, names,
 * tap-adjustable quantities, remove ✕, then a single Confirm. On Confirm the kiosk emits
 * `order-confirmed {items:[{sku, qty}]}` with EXACTLY what is displayed — the order becomes
 * ground-truthed by explicit visual confirmation (fixes audit finding #1: shown-not-tapped
 * pollution of leads.productsInterested).
 *
 * Voice sets quantities ("three of the brass planters"), touch corrects (steppers). Quantities
 * can also arrive from the server via the scene payload. This module keeps the qty arithmetic
 * pure so it's unit-testable and never lets a quantity go below 1 (remove ✕ deletes the line).
 */

import type { ProductCardPayload } from '@coldpress-voice/shared-types';
import { skuKey } from './skuKey';
import { isOnStand } from './synergyR11';

export type OrderLine = {
  sku: string;
  name: string;
  category: string;
  image: string | null;
  qty: number;
  /** R11 #3 — this piece is on the booth stand (exhibited set). Drives the S6 "on the stand" mark. */
  onStand: boolean;
};

export const MIN_QTY = 1;
/** A sane upper guard for the stepper — export orders are large but the kiosk qty is an enquiry hint. */
export const MAX_QTY = 999;

export function clampQty(qty: number): number {
  if (!Number.isFinite(qty)) return MIN_QTY;
  return Math.max(MIN_QTY, Math.min(MAX_QTY, Math.round(qty)));
}

/**
 * R18 X1 — the per-SKU name/image the SERVER resolved for the order (contract §14 W3 extension:
 * `order_review` + `ui.order_committed` items become `{sku, qty, name, image}`). This is the FIRST
 * source for an order line's label/thumbnail; the local card cache is the second source. Field
 * evidence (dev-80, "Jessie/Oklahoma Decor"): 4 planters whose real names live server-side rendered
 * as "Planters piece" because the kiosk only had `{sku, qty}` and no matching card in the cache
 * (voice-driven order, cards scrolled off) → the R17 SKU-like fallback fired. The server now carries
 * the names; fold them in so the real name/image win.
 */
export type OrderItemMeta = { name?: string; image?: string | null };

/**
 * Build the initial order lines from the selected SKUs + the server-resolved item meta + the
 * last-shown cards + (R60 F1) the chip-known names. Name precedence: server meta > card cache >
 * chip name (`ackedSelections`) > card description > the raw SKU,
 * which the R17 `displayPieceName` fallback then renders gracefully. So a voice-driven order whose
 * cards have scrolled off still shows the real name the server resolved, and only a genuinely
 * name-less SKU (absent from BOTH sources) degrades to the "<Category> piece" fallback.
 * Quantities default to 1; any `seedQty` (from a voice-driven scene payload) is applied + clamped.
 * SKUs with no matching card/meta still appear (name falls back to the sku) so nothing silently drops.
 */
export function buildOrderLines(
  selectedSkus: string[],
  cardsBySku: Map<string, ProductCardPayload>,
  seedQty?: Record<string, number>,
  itemMeta?: Record<string, OrderItemMeta>,
  chipNames?: Record<string, string>
): OrderLine[] {
  // R51 (T2): re-key both join sources by the canonical sku_key so a lookup resolves even when the
  // selected sku differs from a card/meta key only by whitespace/case (the exact-string join was the
  // S6 miss: a voice-driven order's sku from the LLM vs the D1-stored card sku). Join-key only — the
  // line's displayed/emitted `sku` stays the original selected string.
  const cardsByKey = new Map<string, ProductCardPayload>();
  for (const [k, v] of cardsBySku) cardsByKey.set(skuKey(k), v);
  const metaByKey = new Map<string, OrderItemMeta>();
  for (const [k, v] of Object.entries(itemMeta ?? {})) metaByKey.set(skuKey(k), v);
  // R60 (F1): the CHIP-KNOWN name map (`ackedSelections`: sku → display name, captured at TAP time
  // and never rotated). The card cache is rebuilt from the LAST `ui.show_products` grid only, so a
  // piece tapped from an EARLIER grid has no card once the visitor moves on — dev-120 hi rendered
  // three of four planters as "Selected piece" on S6 while the right-rail chips showed all four real
  // names, from this very map. It is therefore the LAST name source before the raw SKU.
  const chipByKey = new Map<string, string>();
  for (const [k, v] of Object.entries(chipNames ?? {})) {
    if (typeof v === 'string' && v.trim() !== '') chipByKey.set(skuKey(k), v);
  }
  return selectedSkus.map((sku) => {
    const key = skuKey(sku);
    const card = cardsByKey.get(key);
    const meta = metaByKey.get(key);
    const chipName = chipByKey.get(key) ?? null;
    // R18 X1 — server-resolved name/image are the first source; a blank/whitespace name is treated
    // as absent so it never masks a good card name. The card cache is the second source; the raw
    // SKU is the last resort (rendered via displayPieceName, never shown verbatim).
    const serverName = typeof meta?.name === 'string' && meta.name.trim() !== '' ? meta.name : null;
    const serverImage =
      typeof meta?.image === 'string' && meta.image.trim() !== '' ? meta.image : null;
    return {
      sku,
      // R60 (F1): …then the chip-known name (tap-time truth) before the raw SKU. A resolve MISS now
      // shows the name the visitor saw — `displayPieceName`'s "Selected piece" is the true last resort
      // (a piece with no name ANYWHERE), not the normal outcome of a rotated card cache.
      name: serverName ?? card?.name ?? chipName ?? card?.description ?? sku,
      category: card?.category ?? '',
      image: serverImage ?? card?.image?.url ?? null,
      qty: clampQty(seedQty?.[sku] ?? MIN_QTY),
      onStand: isOnStand(card),
    };
  });
}

/** Set a line's quantity (from the stepper or a voice qty), clamped to [MIN_QTY, MAX_QTY]. */
export function setLineQty(lines: OrderLine[], sku: string, qty: number): OrderLine[] {
  return lines.map((l) => (l.sku === sku ? { ...l, qty: clampQty(qty) } : l));
}

/** Nudge a line up/down by one, clamped. */
export function stepLineQty(lines: OrderLine[], sku: string, delta: number): OrderLine[] {
  return lines.map((l) => (l.sku === sku ? { ...l, qty: clampQty(l.qty + delta) } : l));
}

/** Remove a line entirely (the ✕ affordance). */
export function removeLine(lines: OrderLine[], sku: string): OrderLine[] {
  return lines.filter((l) => l.sku !== sku);
}

/** The `order-confirmed` payload — exactly what is displayed (sku + qty), nothing inferred. */
export function buildOrderConfirmation(lines: OrderLine[]): {
  items: Array<{ sku: string; qty: number }>;
} {
  return { items: lines.map((l) => ({ sku: l.sku, qty: l.qty })) };
}

/**
 * R16 V1 — the live review MIRROR payload for `order-review-updated {items:[{sku, qty}]}` (contract
 * §14). Structurally identical to `buildOrderConfirmation` — the mirror IS the screen's exact state
 * (sku + qty, nothing inferred). The kiosk emits it on S6 seed and on every stepper/removal change,
 * plus a final emit right before `order-confirmed`, so BOTH confirm paths (voice "that's right" AND
 * the Confirm tap) commit from the same source of truth. Named separately from the confirm builder
 * so the emission's intent reads clearly at the call sites.
 */
export function buildReviewMirror(lines: OrderLine[]): {
  items: Array<{ sku: string; qty: number }>;
} {
  return { items: lines.map((l) => ({ sku: l.sku, qty: l.qty })) };
}

export function orderTotalUnits(lines: OrderLine[]): number {
  return lines.reduce((sum, l) => sum + l.qty, 0);
}

/**
 * R13 U4 — parse the server-provided per-item quantity defaults from a `ui.scene
 * {scene:'order_review', payload}` message (voice order: "one sample each" / "two of the first").
 *
 * Tolerant of nesting + item shape (the same seam discipline as ui.categories / ui.contact_field):
 * the items may sit at `payload.items` OR nested under `payload.payload.items`, and each item may
 * carry its sku under `sku`/`id` and its qty under `qty`/`quantity`. Returns a sku→qty map (clamped)
 * for buildOrderLines' seedQty; an empty map defaults every line to 1. Never throws on bad input.
 */
export function parseOrderReviewQty(payload: unknown): Record<string, number> {
  const out: Record<string, number> = {};
  if (!payload || typeof payload !== 'object') return out;
  type Shape = { items?: unknown };
  const outer = payload as Shape & { payload?: unknown };
  const nested =
    outer.payload && typeof outer.payload === 'object' ? (outer.payload as Shape) : ({} as Shape);
  const items = Array.isArray(outer.items)
    ? outer.items
    : Array.isArray(nested.items)
      ? nested.items
      : null;
  if (!items) return out;
  for (const raw of items) {
    if (!raw || typeof raw !== 'object') continue;
    const it = raw as { sku?: unknown; id?: unknown; qty?: unknown; quantity?: unknown };
    const sku =
      typeof it.sku === 'string' && it.sku.trim() !== ''
        ? it.sku.trim()
        : typeof it.id === 'string' && it.id.trim() !== ''
          ? it.id.trim()
          : null;
    if (!sku) continue;
    const rawQty =
      typeof it.qty === 'number' ? it.qty : typeof it.quantity === 'number' ? it.quantity : null;
    if (rawQty === null) continue;
    out[sku] = clampQty(rawQty);
  }
  return out;
}

/**
 * R18 X1 — parse the server-resolved per-item name/image from an `order_review` scene payload OR a
 * `ui.order_committed` message (contract §14 W3 extension: items carry `{sku, qty, name, image}`).
 * Returns a sku→{name, image} map for `buildOrderLines`' `itemMeta`. Tolerant of the SAME nestings
 * + sku aliases as `parseOrderReviewQty` (items at `payload.items` or `payload.payload.items`; sku
 * under `sku`/`id`), plus tolerant name/image keys (`name`|`title`, `image`|`image_url`|`img`). A
 * blank name/image is omitted (so it never masks a good card value downstream). Items with neither
 * a name nor an image are skipped entirely (nothing to enrich). Never throws on bad input.
 */
export function parseOrderReviewItems(payload: unknown): Record<string, OrderItemMeta> {
  const out: Record<string, OrderItemMeta> = {};
  if (!payload || typeof payload !== 'object') return out;
  type Shape = { items?: unknown };
  const outer = payload as Shape & { payload?: unknown };
  const nested =
    outer.payload && typeof outer.payload === 'object' ? (outer.payload as Shape) : ({} as Shape);
  const items = Array.isArray(outer.items)
    ? outer.items
    : Array.isArray(nested.items)
      ? nested.items
      : null;
  if (!items) return out;
  for (const raw of items) {
    if (!raw || typeof raw !== 'object') continue;
    const it = raw as {
      sku?: unknown;
      id?: unknown;
      name?: unknown;
      title?: unknown;
      image?: unknown;
      image_url?: unknown;
      img?: unknown;
    };
    const sku =
      typeof it.sku === 'string' && it.sku.trim() !== ''
        ? it.sku.trim()
        : typeof it.id === 'string' && it.id.trim() !== ''
          ? it.id.trim()
          : null;
    if (!sku) continue;
    const name = firstNonBlankString(it.name, it.title);
    const image = firstNonBlankString(it.image, it.image_url, it.img);
    if (name === null && image === null) continue; // nothing to enrich with
    out[sku] = {
      ...(name !== null ? { name } : {}),
      ...(image !== null ? { image } : {}),
    };
  }
  return out;
}

/**
 * R19 — the DEFENSIVE S6 seed set: the SKUs the review card must show, guaranteed never empty when
 * anything is selected. An `order_review` scene must NEVER render "dead" plain cards / a bare empty
 * state after an order intent (dev-81, "Shuchi/Delhi Decor": the visitor saw product cards, no
 * review UI). The tapped SKUs are ALWAYS known client-side (the selection tray + the server-acked
 * selections), so even if the scene's `payload.items` is missing, empty, or unparseable, S6 still
 * seeds from the local tray.
 *
 * Precedence + union (dedup, order-stable):
 *   1. the scene payload's parsed items (server's proposed set, when present) — first, in payload
 *      order, so a voice order "these three" seeds in the order Gigi proposed;
 *   2. the optimistic local selection tray (`selectedSkus`);
 *   3. the server-acked selections (`ackedSelectionSkus`) — a belt-and-braces floor for a tray that
 *      was cleared/raced but whose acks survive;
 *   4. R20 (U2) — the re-emitted showcase cards (`cardFallbackSkus`), the RECONNECT floor. On a fresh
 *      page load mid-conversation the tray + acks are empty (nothing tapped this load) and the
 *      re-emitted `order_review` scene is BARE (its items ride the separate `ui.show_products`
 *      re-emit, not the scene payload) — so sources 1–3 are all empty and S6 would render its "nothing
 *      selected yet" empty state DESPITE a full order being rehydrated. The re-emitted cards are that
 *      order's items, so they seed the review rows. Last in precedence + still order-stable, so in the
 *      normal (non-reconnect) case where 1–3 already carry the selection, these only append genuinely
 *      new SKUs and never reorder a live tap-selection.
 * Any SKU seen earlier is not repeated. Returns [] only when genuinely nothing is selected anywhere AND
 * no cards were re-emitted (then S6 legitimately shows its "nothing selected yet" empty state).
 */
export function orderReviewSeedSkus(
  scenePayload: unknown,
  selectedSkus: readonly string[],
  ackedSelectionSkus: readonly string[] = [],
  cardFallbackSkus: readonly string[] = []
): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  const push = (sku: unknown) => {
    if (typeof sku !== 'string') return;
    const s = sku.trim();
    if (s === '' || seen.has(s)) return;
    seen.add(s);
    out.push(s);
  };
  // 1. scene payload items — parseOrderReviewQty yields SKUs that carried a qty; parseOrderReviewItems
  // folds in any bare `{sku, name}` proposal SKUs that had no qty. Both are tolerant of top-level or
  // nested `payload.items` and sku/id aliases.
  for (const sku of Object.keys(parseOrderReviewQty(scenePayload))) push(sku);
  for (const sku of Object.keys(parseOrderReviewItems(scenePayload))) push(sku);
  // 2 + 3. the local tray, then the acked floor.
  for (const sku of selectedSkus) push(sku);
  for (const sku of ackedSelectionSkus) push(sku);
  // 4. R20 (U2) — the reconnect floor: the re-emitted showcase cards. Only appends SKUs not already
  // seeded, so a live tap-selection (1–3) always leads and this never reorders it.
  for (const sku of cardFallbackSkus) push(sku);
  return out;
}

/** First value that is a non-blank string, else null. */
function firstNonBlankString(...vals: unknown[]): string | null {
  for (const v of vals) {
    if (typeof v === 'string' && v.trim() !== '') return v.trim();
  }
  return null;
}
